import React, { useEffect, useState } from "react";
import { CalendarRange, Download, Loader2 } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";

function iso(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

function currentPeriod() {
  const now = new Date();
  let start;
  let end;
  if (now.getDate() >= 28) {
    start = new Date(now.getFullYear(), now.getMonth(), 28);
    end = new Date(now.getFullYear(), now.getMonth() + 1, 27);
  } else {
    start = new Date(now.getFullYear(), now.getMonth() - 1, 28);
    end = new Date(now.getFullYear(), now.getMonth(), 27);
  }
  return { start: iso(start), end: iso(end) };
}

export default function MonthlySummary() {
  const initial = currentPeriod();
  const [periodStart, setPeriodStart] = useState(initial.start);
  const [periodEnd, setPeriodEnd] = useState(initial.end);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    api.get("/monthly/my-summary", { params: { period_start: periodStart, period_end: periodEnd } })
      .then((r) => setData(r.data))
      .catch((err) => toast.error(formatApiError(err)))
      .finally(() => setLoading(false));
  }, [periodStart, periodEnd]);

  const s = data?.summary;
  async function downloadPdf() {
    try {
      const response = await api.get("/monthly/my-export", {
        params: { period_start: periodStart, period_end: periodEnd },
        responseType: "blob",
      });
      const url = URL.createObjectURL(response.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `meu-fechamento-${periodStart}-a-${periodEnd}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      toast.error(formatApiError(err));
    }
  }
  return (
    <div className="space-y-5">
      <div>
        <p className="text-[10px] font-bold uppercase tracking-[0.25em] text-blue-400">Período 28 → 27</p>
        <h1 className="mt-1 font-display text-3xl font-bold text-white">Meu fechamento mensal</h1>
        <p className="mt-1 text-sm text-slate-400">Consulte somente os seus números consolidados.</p>
      </div>

      <div className="grid grid-cols-2 gap-3 rounded-2xl border border-slate-800 bg-[#131B2B] p-4">
        <label className="text-xs font-semibold uppercase tracking-wider text-slate-400">Início
          <input type="date" value={periodStart} onChange={(e) => setPeriodStart(e.target.value)} className="mt-2 h-11 w-full rounded-lg border border-slate-700 bg-slate-900 px-3 text-sm text-white" />
        </label>
        <label className="text-xs font-semibold uppercase tracking-wider text-slate-400">Fim
          <input type="date" value={periodEnd} onChange={(e) => setPeriodEnd(e.target.value)} className="mt-2 h-11 w-full rounded-lg border border-slate-700 bg-slate-900 px-3 text-sm text-white" />
        </label>
      </div>

      {loading ? (
        <div className="flex justify-center py-14 text-blue-400"><Loader2 className="h-6 w-6 animate-spin" /></div>
      ) : !s ? (
        <div className="rounded-2xl border border-slate-800 bg-[#131B2B] p-8 text-center text-sm text-slate-400">Nenhum fechamento encontrado neste período.</div>
      ) : (
        <>
          <div className="grid grid-cols-2 gap-3">
            <Card label="Entregues" value={s.total_entregues} />
            <Card label="Retornos" value={s.total_retornos} />
            <Card label="Fora de lista" value={s.total_fora} />
            <Card label="Coletas" value={s.total_coletas} accent />
            <Card label="Devoluções" value={s.total_devolucoes} />
            <Card label="Remessas AR" value={s.total_ar} />
            <Card label="OnLog" value={s.total_onlog} />
            <Card label="Sucesso" value={`${Number(s.sucesso || 0).toFixed(2)}%`} accent />
          </div>
          <div className="rounded-2xl border border-slate-800 bg-[#131B2B] p-4">
            <div className="flex items-center gap-2 text-blue-300"><CalendarRange className="h-4 w-4" /><span className="text-xs font-bold uppercase tracking-wider">Resumo do período</span></div>
            <div className="mt-4 grid grid-cols-2 gap-4 text-sm">
              <Line label="Dias trabalhados" value={s.dias_trabalhados} />
              <Line label="Dias sem fechamento" value={s.dias_sem_fechamento} />
              <Line label="Total entregues + retornos + fora" value={s.total_geral} />
              <Line label="Situação" value={s.situacao} />
            </div>
          </div>
          <button onClick={downloadPdf} className="flex w-full items-center justify-center gap-2 rounded-xl bg-blue-600 px-4 py-3 text-sm font-bold text-white hover:bg-blue-500">
            <Download className="h-4 w-4" /> Baixar meu fechamento mensal em PDF
          </button>
        </>
      )}
    </div>
  );
}

function Card({ label, value, accent = false }) {
  return <div className="rounded-2xl border border-slate-800 bg-[#131B2B] p-4"><p className="text-[10px] font-bold uppercase tracking-wider text-slate-500">{label}</p><p className={`mt-2 font-mono text-2xl font-bold ${accent ? "text-blue-400" : "text-white"}`}>{value ?? 0}</p></div>;
}

function Line({ label, value }) {
  return <div><p className="text-xs text-slate-500">{label}</p><p className="mt-1 font-semibold text-slate-100">{value ?? 0}</p></div>;
}
